import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonIcon, Card } from "ui-kit-ck-consultant";
import {
  faCaretLeft,
  faCaretRight,
  faTools,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import "moment/locale/fr"; // Import du locale français
import axios from "axios";
import { getStatus, getStatusBadgeClass } from "../../utils/general";
import {
  SLOT_STATES,
  LEGEND_STATES,
  getSlotState,
} from "../../utils/planningSlots";

// Configurer moment en français
moment.locale("fr");

const Planning = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(moment());
  const [appointments, setAppointments] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [manuallyBlockedSlots, setManuallyBlockedSlots] = useState([]);
  // T1 : agence DOM -> planning inversé (tout fermé sauf créneaux ouverts)
  const [isDom, setIsDom] = useState(false);
  const [openSlots, setOpenSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const getCurrentWeek = () => {
    return moment(selectedDate).clone().startOf("week");
  };

  // Fonction pour récupérer les RDV de la semaine et les congés
  const fetchWeekAppointments = async (weekStart) => {
    setLoading(true);
    try {
      const weekEnd = weekStart.clone().endOf("week");

      const response = await axios.get("/api/folder/appointments", {
        params: {
          startDate: weekStart.format("YYYY-MM-DD"),
          endDate: weekEnd.format("YYYY-MM-DD"),
        },
      });

      if (response.data && response.data.success) {
        setAppointments(response.data.appointments || []);
        setHolidays(response.data.holidays || []);
        setManuallyBlockedSlots(response.data.blockedSlots || []);
        setIsDom(response.data.isDom || false);
        setOpenSlots(response.data.openSlots || []);
      } else {
        setAppointments([]);
        setHolidays([]);
        setManuallyBlockedSlots([]);
        setIsDom(false);
        setOpenSlots([]);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des RDV:", error);
      setAppointments([]);
      setHolidays([]);
      setManuallyBlockedSlots([]);
      setIsDom(false);
      setOpenSlots([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger les RDV au montage du composant et quand la semaine change
  useEffect(() => {
    const currentWeek = moment(selectedDate).clone().startOf("week");
    fetchWeekAppointments(currentWeek);
  }, [selectedDate]);

  const daysOpen = [1, 2, 3, 4, 5];
  const timeSlots = [
    { hour: 9, minute: 0, display: "9h00" },
    { hour: 9, minute: 45, display: "9h45" },
    { hour: 10, minute: 30, display: "10h30" },
    { hour: 11, minute: 15, display: "11h15" },
    { hour: 14, minute: 0, display: "14h00" },
    { hour: 14, minute: 45, display: "14h45" },
    { hour: 15, minute: 30, display: "15h30" },
    { hour: 16, minute: 15, display: "16h15" },
  ];

  let days = [
    ...(daysOpen.includes(1)
      ? [getCurrentWeek().clone().add(0, "day").format("YYYY-MM-DD")]
      : []),
    ...(daysOpen.includes(2)
      ? [getCurrentWeek().clone().add(1, "day").format("YYYY-MM-DD")]
      : []),
    ...(daysOpen.includes(3)
      ? [getCurrentWeek().clone().add(2, "day").format("YYYY-MM-DD")]
      : []),
    ...(daysOpen.includes(4)
      ? [getCurrentWeek().clone().add(3, "day").format("YYYY-MM-DD")]
      : []),
    ...(daysOpen.includes(5)
      ? [getCurrentWeek().clone().add(4, "day").format("YYYY-MM-DD")]
      : []),
  ];

  // Créer la liste des créneaux bloqués à partir des RDV de la base
  const blockedSlots = appointments.map((appointment) => {
    const appointmentDate = moment(appointment.f_date_appointment).format(
      "YYYY-MM-DD"
    );
    const appointmentTime = appointment.f_hour_appointment;
    return `${appointmentDate} ${appointmentTime}`;
  });

  const isDateIn = (periods, dateToCheck) => {
    const checkDate = moment(dateToCheck);
    return periods.some((period) => {
      const startDate = moment(period.dateStart);
      const endDate = moment(period.dateEnd);
      return (
        checkDate.isSameOrAfter(startDate, "day") &&
        checkDate.isSameOrBefore(endDate, "day")
      );
    });
  };

  const publicHolidayDays = days.filter((day) =>
    isDateIn(holidays.filter((h) => h.isPublicHoliday), day)
  );
  const leaveDays = days.filter((day) =>
    isDateIn(holidays.filter((h) => !h.isPublicHoliday), day)
  );

  // Fonction pour récupérer les RDV d'un créneau spécifique
  const getAppointmentsForSlot = (day, timeSlot) => {
    const timeString = `${timeSlot.hour
      .toString()
      .padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}`;

    return appointments.filter((app) => {
      const appointmentDate = moment(app.f_date_appointment).format("YYYY-MM-DD");
      const appointmentTime = app.f_hour_appointment;
      return `${appointmentDate} ${appointmentTime}` === `${day} ${timeString}`;
    });
  };

  // Fonction pour vérifier si un créneau est manuellement bloqué
  const isSlotManuallyBlocked = (day, timeSlot) => {
    const timeString = `${timeSlot.hour
      .toString()
      .padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}:00`;

    return manuallyBlockedSlots.some((slot) => {
      return slot.date === day && slot.time === timeString;
    });
  };

  // Fonction pour vérifier si un créneau est ouvert (agence DOM)
  const isSlotOpened = (day, timeSlot) => {
    const timeString = `${timeSlot.hour
      .toString()
      .padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}:00`;

    return openSlots.some((slot) => {
      return slot.date === day && slot.time === timeString;
    });
  };

  // Fonction pour bloquer un créneau
  const blockSlot = async (day, timeSlot) => {
    const timeString = `${timeSlot.hour
      .toString()
      .padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}:00`;

    try {
      const response = await axios.post("/api/folder/block-slot", {
        date: day,
        time: timeString,
        reason: "Indisponible",
      });

      if (response.data && response.data.success) {
        // Rafraîchir les données
        const currentWeek = moment(selectedDate).clone().startOf("week");
        fetchWeekAppointments(currentWeek);
        setShowBlockDialog(false);
        setSelectedSlot(null);
      }
    } catch (error) {
      console.error("Erreur lors du blocage du créneau:", error);
    }
  };

  // Fonction pour débloquer un créneau
  const unblockSlot = async (day, timeSlot) => {
    const timeString = `${timeSlot.hour
      .toString()
      .padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}:00`;

    try {
      const response = await axios.post("/api/folder/unblock-slot", {
        date: day,
        time: timeString,
      });

      if (response.data && response.data.success) {
        // Rafraîchir les données
        const currentWeek = moment(selectedDate).clone().startOf("week");
        fetchWeekAppointments(currentWeek);
      }
    } catch (error) {
      console.error("Erreur lors du déblocage du créneau:", error);
    }
  };

  // Fonction pour ouvrir un créneau (agence DOM)
  const openSlot = async (day, timeSlot) => {
    const timeString = `${timeSlot.hour
      .toString()
      .padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}:00`;

    try {
      const response = await axios.post("/api/folder/open-slot", {
        date: day,
        time: timeString,
        reason: "Ouvert",
      });

      if (response.data && response.data.success) {
        const currentWeek = moment(selectedDate).clone().startOf("week");
        fetchWeekAppointments(currentWeek);
      }
    } catch (error) {
      console.error("Erreur lors de l'ouverture du créneau:", error);
    }
  };

  // Fonction pour refermer un créneau (agence DOM)
  const closeSlot = async (day, timeSlot) => {
    const timeString = `${timeSlot.hour
      .toString()
      .padStart(2, "0")}:${timeSlot.minute.toString().padStart(2, "0")}:00`;

    try {
      const response = await axios.post("/api/folder/close-slot", {
        date: day,
        time: timeString,
      });

      if (response.data && response.data.success) {
        const currentWeek = moment(selectedDate).clone().startOf("week");
        fetchWeekAppointments(currentWeek);
      }
    } catch (error) {
      console.error("Erreur lors de la fermeture du créneau:", error);
    }
  };

  const cancelAppointment = async (folderId) => {
    try {
      const response = await axios.post("/api/folder/cancel-appointment", {
        id: folderId,
      });
      if (response.data && response.data.success) {
        fetchWeekAppointments(getCurrentWeek());
        setShowPopup(false);
      } else {
        alert(
          (response.data && response.data.message) ||
            "Une erreur est survenue."
        );
      }
    } catch (error) {
      console.error("Erreur lors de l'annulation du rendez-vous:", error);
    }
  };

  const isCanceled = (appointment) => !!appointment.f_appointment_canceled;

  // Fonction pour gérer le clic sur un créneau
  const handleSlotClick = (day, timeSlot) => {
    const slotAppointments = getAppointmentsForSlot(day, timeSlot);

    if (slotAppointments.length === 1) {
      setSelectedAppointment(slotAppointments[0]);
      setShowPopup(true);
    } else if (slotAppointments.length > 1) {
      // Pour plusieurs RDV, afficher une liste
      setSelectedAppointment({
        multiple: true,
        appointments: slotAppointments,
        timeSlot: timeSlot.display,
        date: moment(day).format("dddd DD MMMM YYYY"),
      });
      setShowPopup(true);
    }
  };

  // Fonction pour gérer le clic droit sur un créneau (bloquer/débloquer)
  const handleSlotRightClick = (e, day, timeSlot) => {
    e.preventDefault();
    const hasAppointments = getAppointmentsForSlot(day, timeSlot).some(
      (app) => !isCanceled(app)
    );

    // Ne pas permettre de modifier un créneau qui a déjà des RDV
    if (hasAppointments) return;

    // T1 : agence DOM -> le clic droit ouvre / referme le créneau
    if (isDom) {
      if (isSlotOpened(day, timeSlot)) {
        closeSlot(day, timeSlot);
      } else {
        openSlot(day, timeSlot);
      }
      return;
    }

    if (isSlotManuallyBlocked(day, timeSlot)) {
      unblockSlot(day, timeSlot);
    } else {
      setSelectedSlot({ day, timeSlot });
      setShowBlockDialog(true);
    }
  };

  const getElement = (day, timeSlot) => {
    const slotAppointments = getAppointmentsForSlot(day, timeSlot);
    const appointmentCount = slotAppointments.length;
    const state = getSlotState({
      liveAppointments: slotAppointments.some((app) => !isCanceled(app)),
      canceledAppointments: appointmentCount > 0,
      isPublicHoliday: publicHolidayDays.includes(day),
      isLeave: leaveDays.includes(day),
      isDom,
      isOpened: isSlotOpened(day, timeSlot),
      isBlocked: isSlotManuallyBlocked(day, timeSlot),
    });
    const { color } = SLOT_STATES[state];
    const isClickable = state === "booked" || state === "canceled";

    let displayText = timeSlot.display;
    let titleText = {
      publicHoliday: "Jour férié",
      leave: "Jour de congé",
      canceled: "Rendez-vous annulé - créneau disponible",
      booked: "Cliquer pour voir les détails du rendez-vous",
      closed: "Créneau fermé - Clic droit pour ouvrir",
      blocked: "Créneau bloqué - Clic droit pour débloquer",
      free: isDom
        ? "Créneau ouvert - Clic droit pour refermer"
        : "Clic droit pour bloquer ce créneau",
    }[state];
    if (state === "booked" && appointmentCount > 1) {
      displayText = `${timeSlot.display} (${appointmentCount})`;
      titleText = `${appointmentCount} rendez-vous - Cliquer pour voir les détails`;
    }
    if (state === "booked" && publicHolidayDays.includes(day)) {
      titleText = `Rendez-vous un jour férié - ${titleText}`;
    } else if (state === "booked" && leaveDays.includes(day)) {
      titleText = `Rendez-vous pendant un congé - ${titleText}`;
    }

    return (
      <div
        className={`text-center text-white rounded d-flex align-items-center justify-content-center ${
          isClickable ? "cursor-pointer hover:opacity-80" : "cursor-pointer"
        }`}
        style={{
          backgroundColor: color,
          minHeight: "35px",
          margin: "2px",
          fontSize: "12px",
          fontWeight: "500",
          border: "1px solid rgba(255,255,255,0.2)",
        }}
        onClick={isClickable ? () => handleSlotClick(day, timeSlot) : undefined}
        onContextMenu={(e) => handleSlotRightClick(e, day, timeSlot)}
        title={titleText}
      >
        <span>{displayText}</span>
      </div>
    );
  };

  return (
    <>
      <Card
        title="Planning"
        className="mb-10 mb-20-xl"
        classNameChildren="overflow-x-auto"
        disableStyleAction
        action={
          <>
            <ButtonIcon
              onClick={() => {
                setSelectedDate(
                  moment(selectedDate).clone().subtract(1, "week")
                );
              }}
              icon={faTools}
              className="blue"
            >
              <FontAwesomeIcon icon={faCaretLeft} />
            </ButtonIcon>
            <span>{getCurrentWeek().format("DD/MM/YYYY")}</span> -{" "}
            <span>
              {getCurrentWeek().clone().endOf("week").format("DD/MM/YYYY")}
            </span>
            <ButtonIcon
              onClick={() => {
                setSelectedDate(moment(selectedDate).clone().add(1, "week"));
              }}
              icon={faTools}
              className="blue"
            >
              <FontAwesomeIcon icon={faCaretRight} />
            </ButtonIcon>
          </>
        }
      >
        {loading && (
          <div className="text-center p-4">
            <span>Chargement des rendez-vous...</span>
          </div>
        )}

        {/* T1 : sans l'indication, le clic droit est indécouvrable alors que
            c'est l'action principale de ces agences. */}
        {isDom && (
          <div
            style={{
              margin: "0 5px 10px",
              padding: "8px 12px",
              borderRadius: 6,
              backgroundColor: "#fff4e5",
              border: "1px solid #ffd8a8",
              fontSize: 13,
            }}
          >
            Cette agence n'a <b>pas de créneaux automatiques</b> : tous les
            créneaux sont fermés par défaut.{" "}
            <b>Clic droit sur un créneau pour l'ouvrir</b> (ou le refermer). Seuls
            les créneaux ouverts sont réservables par le client.
          </div>
        )}

        <table className="table-garage">
          <thead>
            <tr>
              {days.map((day, idxDay) => (
                <th key={idxDay}>
                  <div className="m-5 d-flex gap-5 align-center flex-column">
                    <span>
                      {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi"][idxDay]} ({moment(day).format("DD/MM/YYYY")})
                    </span>
                  </div>
                </th>
              ))}
            </tr>
            <tr></tr>
          </thead>
          <tbody>
            {timeSlots.map((timeSlot, idxSlot) => (
              <tr key={idxSlot}>
                {days.map((day, idxDay) => (
                  <td className="no-border" key={idxDay}>
                    <div className="d-flex justify-content-center">
                      {getElement(day, timeSlot)}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="d-flex" style={{ gap: 10, margin: 5, flexWrap: "wrap" }}>
          {LEGEND_STATES.filter((state) =>
            isDom ? state !== "blocked" : state !== "closed"
          ).map((state) => (
            <div
              key={state}
              className="d-flex"
              style={{ alignItems: "center", gap: 10 }}
            >
              <div
                style={{
                  backgroundColor: SLOT_STATES[state].color,
                  width: 40,
                  height: 14,
                  borderRadius: 8,
                }}
              ></div>
              <span style={{ fontSize: 14 }}>
                {state === "free" && isDom ? "Ouvert" : SLOT_STATES[state].label}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Popup de détails du rendez-vous */}
      {showPopup && selectedAppointment && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setShowPopup(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedAppointment.multiple
                  ? `Rendez-vous multiples (${selectedAppointment.appointments.length})`
                  : "Détails du rendez-vous"}
              </h3>
              <button
                onClick={() => setShowPopup(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>

            {selectedAppointment.multiple ? (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-600">
                    {selectedAppointment.date} à {selectedAppointment.timeSlot}
                  </p>
                </div>

                {selectedAppointment.appointments.map((appointment, index) => (
                  <div key={index} className="border-b pb-4 last:border-b-0">
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs font-medium text-gray-600">
                          Référence :
                        </label>
                        <p className="text-sm text-gray-900">
                          {appointment.reference}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">
                          Client :
                        </label>
                        <p className="text-sm text-gray-900">
                          {appointment.insuredName}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">
                          Expert :
                        </label>
                        <p className="text-sm text-gray-900">
                          {appointment.expertName || "Non assigné"}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">
                          Statut du dossier :
                        </label>
                        <span
                          className={`px-2 py-1 rounded text-xs ${getStatusBadgeClass(
                            appointment.status
                          )}`}
                        >
                          {getStatus(appointment.status)}
                        </span>
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => {
                            navigate(`/folder/${appointment.token}`);
                            setShowPopup(false);
                          }}
                          className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Voir le dossier
                        </button>
                        {isCanceled(appointment) ? (
                          <span className="text-xs px-3 py-1 bg-gray-100 text-gray-800 rounded">
                            Annulé
                          </span>
                        ) : (
                          <button
                            onClick={() => cancelAppointment(appointment.id)}
                            className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          >
                            Annuler le RDV
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Référence :
                  </label>
                  <p className="text-gray-900">
                    {selectedAppointment.reference}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Client :
                  </label>
                  <p className="text-gray-900">
                    {selectedAppointment.insuredName}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Date :
                  </label>
                  <p className="text-gray-900">
                    {moment(selectedAppointment.f_date_appointment).format(
                      "dddd DD MMMM YYYY"
                    )}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Heure :
                  </label>
                  <p className="text-gray-900">
                    {selectedAppointment.f_hour_appointment}
                    {isCanceled(selectedAppointment) ? (
                      <span className="ml-2 px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                        Annulé
                      </span>
                    ) : null}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Statut du dossier :
                  </label>
                  <p className="text-gray-900">
                    <span
                      className={`px-2 py-1 rounded text-xs ${getStatusBadgeClass(
                        selectedAppointment.status
                      )}`}
                    >
                      {getStatus(selectedAppointment.status)}
                    </span>
                  </p>
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowPopup(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
              {!selectedAppointment.multiple && (
                <button
                  onClick={() => {
                    navigate(`/folder/${selectedAppointment.token}`);
                    setShowPopup(false);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Voir le dossier
                </button>
              )}
              {!selectedAppointment.multiple &&
                !isCanceled(selectedAppointment) && (
                  <button
                    onClick={() => cancelAppointment(selectedAppointment.id)}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Annuler le RDV
                  </button>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmation pour bloquer un créneau */}
      {showBlockDialog && selectedSlot && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => {
            setShowBlockDialog(false);
            setSelectedSlot(null);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Bloquer ce créneau
              </h3>
              <button
                onClick={() => {
                  setShowBlockDialog(false);
                  setSelectedSlot(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="space-y-3 mb-6">
              <p className="text-sm text-gray-600">
                Voulez-vous bloquer ce créneau ?
              </p>
              <div className="bg-gray-100 p-3 rounded">
                <p className="text-sm font-medium text-gray-900">
                  {moment(selectedSlot.day).format("dddd DD MMMM YYYY")}
                </p>
                <p className="text-sm text-gray-600">
                  {selectedSlot.timeSlot.display}
                </p>
              </div>
              <p className="text-xs text-gray-500">
                Une fois bloqué, ce créneau ne sera plus disponible pour les
                prises de rendez-vous.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowBlockDialog(false);
                  setSelectedSlot(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() =>
                  blockSlot(selectedSlot.day, selectedSlot.timeSlot)
                }
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Bloquer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Planning;
